import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Award, Users, Globe, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Quality Excellence',
      description: 'We maintain the highest standards in every product and service we deliver.',
    },
    {
      icon: Heart,
      title: 'Customer First',
      description: 'Our clients\' success is our success. We build lasting partnerships.',
    },
    {
      icon: Users,
      title: 'Team Spirit',
      description: 'Our skilled workforce is our greatest asset. We invest in people.',
    },
    {
      icon: Globe,
      title: 'Global Vision',
      description: 'We think globally while delivering locally with international standards.',
    },
  ];

  const timeline = [
    { year: '2014', title: 'Foundation', description: 'AUMKAR Group established with Aumkar International in Dundigal, India.' },
    { year: '2017', title: 'Expansion', description: 'Expanded manufacturing capacity and product range.' },
    { year: '2019', title: 'International Growth', description: 'Established service operations in Oman with Remas Cleaning Services.' },
    { year: '2021', title: 'Diversification', description: 'Launched Goodluck Laundry Services in Oman.' },
    { year: '2024', title: 'Continued Growth', description: 'Strengthening our presence across India and Oman.' },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
                About Us
              </p>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 mb-6">
                Building Tomorrow's Industries Today
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                AUMKAR Group of Companies is a diversified business conglomerate with operations spanning manufacturing and services across India and Oman.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our flagship company, <strong>Aumkar International</strong>, based in Dundigal, India, specializes in packaging manufacturing, producing high-quality cardboard boxes, wrappers, and bulk packaging materials for various industries.
              </p>
              <Link to="/companies">
                <Button
                  data-testid="about-explore-btn"
                  className="bg-blue-700 hover:bg-blue-800 text-white rounded-full group"
                >
                  Explore Our Companies
                  <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-blue-100 rounded-2xl -z-10" />
              <img
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg"
                alt="AUMKAR Group team"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="vision-mission-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-slate-200 card-hover">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-blue-700" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed">
                  To become a global multi-industry business group recognized for excellence, innovation, and sustainable practices in manufacturing and services.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 card-hover">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-blue-700" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
                <p className="text-slate-600 leading-relaxed">
                  To deliver quality products and services with excellence, creating value for our customers, employees, and communities while maintaining the highest ethical standards.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section data-testid="leadership-section" className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-400 mb-4">
              Leadership
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Meet Our Leaders
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Visionary leaders who turned dreams into a global business empire through hard work, determination, and unwavering commitment to excellence.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Managing Director - Ashok Poojary */}
            <Card className="bg-slate-800 border-slate-700 overflow-hidden">
              <div className="aspect-square relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_packaging-services/artifacts/wveb3nt3_image.png"
                  alt="Mr. Ashok Poojary"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-1 text-white">Mr. Ashok Poojary</h3>
                <p className="text-blue-400 text-sm mb-3">Founder & Managing Director</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  A powerful-minded visionary who rose from humble beginnings in a small Indian village. Driven by ambition and an unshakeable spirit, he journeyed to Oman to build his dreams. Through years of struggle and perseverance, he transformed his vision into reality, co-founding AUMKAR Group with his trusted partner. His story is a testament to the power of determination and the belief that no dream is too big.
                </p>
                <p className="text-slate-400 text-xs">+91 90362 40229</p>
              </CardContent>
            </Card>

            {/* Mamon Chowdhury */}
            <Card className="bg-slate-800 border-slate-700 overflow-hidden">
              <div className="aspect-square relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_packaging-services/artifacts/owhjr6kn_image.png"
                  alt="Mrs. Mamon Chowdhury"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-1 text-white">Mrs. Mamon Chowdhury</h3>
                <p className="text-blue-400 text-sm mb-3">Oman Partner & Vice Director</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  The "Lady of Rock" — a dynamic leader whose powerful business journey has inspired many. With exceptional leadership skills and an iron will, she has been instrumental in establishing and growing AUMKAR's service operations in Oman. Her strategic vision and tireless dedication have helped transform the company into a trusted name in cleaning and laundry services across the region.
                </p>
                <p className="text-slate-400 text-xs">+968 9490 0565</p>
              </CardContent>
            </Card>

            {/* M Laxmipathi */}
            <Card className="bg-slate-800 border-slate-700 overflow-hidden">
              <div className="aspect-square relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_packaging-services/artifacts/5pfa875e_image.png"
                  alt="Mr. M Laxmipathi"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-1 text-white">Mr. M Laxmipathi</h3>
                <p className="text-blue-400 text-sm mb-3">Managing Director & Director of Headquarters</p>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  The backbone of AUMKAR's India operations. As the India-based partner and Director of Headquarters at Dundigal, he oversees the flagship packaging manufacturing business. His hands-on approach and deep understanding of the industry ensure seamless operations, quality production, and continued growth of Aumkar International.
                </p>
                <p className="text-slate-400 text-xs">+91 62817 56651</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
              Our Values
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              What Drives Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-slate-200 card-hover text-center">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <value.icon className="w-7 h-7 text-blue-700" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section data-testid="timeline-section" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
              Our Journey
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Milestones & Growth
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-blue-200 hidden md:block" />
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  <div className="flex-1 md:text-right">
                    {index % 2 === 0 ? (
                      <Card className="border-slate-200 card-hover">
                        <CardContent className="p-6">
                          <span className="text-sm font-semibold text-blue-700">{item.year}</span>
                          <h3 className="text-lg font-bold text-slate-900 mt-1 mb-2">{item.title}</h3>
                          <p className="text-slate-600 text-sm">{item.description}</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="hidden md:block" />
                    )}
                  </div>
                  <div className="w-4 h-4 bg-blue-700 rounded-full z-10 hidden md:block" />
                  <div className="flex-1">
                    {index % 2 !== 0 ? (
                      <Card className="border-slate-200 card-hover">
                        <CardContent className="p-6">
                          <span className="text-sm font-semibold text-blue-700">{item.year}</span>
                          <h3 className="text-lg font-bold text-slate-900 mt-1 mb-2">{item.title}</h3>
                          <p className="text-slate-600 text-sm">{item.description}</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="hidden md:block" />
                    )}
                  </div>
                  {/* Mobile view */}
                  <Card className="border-slate-200 card-hover md:hidden w-full">
                    <CardContent className="p-6">
                      <span className="text-sm font-semibold text-blue-700">{item.year}</span>
                      <h3 className="text-lg font-bold text-slate-900 mt-1 mb-2">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-blue-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            Want to Work With Us?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Join our journey of excellence. Partner with AUMKAR Group for quality products and services.
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-cta-btn"
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-full px-8 py-6 text-base group"
            >
              Get In Touch
              <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
